import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {ModifyReasonsListDataGridCellKeys} from "../../../../core/constants/table/data-grid-column-keys";
import {dateComparator, stringComparator} from "../../../../core/services/utils/utils";
import {DataGridActionTypes, dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, DataGridDensities, DataGridExoticColumnFields} from "../../../../packages/data-grid";
import IconButton from "../../base/button/icon_button";
import {CircularProgress, DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Edit} from "@mui/icons-material";
import {OutlinedInput, OutlinedInputForm} from "../../base/input/outlined_input";
import {Col, Row} from "reactstrap";
import PartnerDataGrid, {useDataGridSavedState} from "../../../containers/data-grid";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import useIsMounted from "../../../hooks/use-is-mounted";
import {ReactComponent as DefaultIcon} from "../../../../assets/images/default-icon.svg";
import Form from "../../base/form";
import * as Yup from "yup";
import {makeRequired, makeValidate} from "mui-rff";
import ObserversService from "../../../../core/services/observers";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../core/services/api/api";
import useAccessLevels from "../../../hooks/use-access-levels";

const _initialPaginationInfo = {
    pageSize: 5,
    currentPage: -1,
    length: 0,
    sizes: [5, 10, 15],
};

const maxCodeLength = 10;
const maxReasonLength = 100;

const dataGridSaveConfig = {
    name: 'ModifyAvailabilityDialogDataGrid',
    version: 1,
};


/**
 * Promotions list action names
 */
const ModifyAvailabilityReasonsListActionNames = {
    SetAsDefault: 'setAsDefault',
    Delete: 'delete'
}

const ModifyAvailabilityReasonsDialog = ({open, setOpen, onDialogClosed, allReasons: allReasonsProp}) => {
    const [allReasons, setAllReasons] = useState(allReasonsProp ?? [])
    const [visibleReasons, setVisibleReasons] = useState([])
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManageAdjustmentReasons = packages.bizInventory.manageAdjustmentReasons;

    /**
     * Memo version of the columns entities for data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /**@type {DataGridColumn[]}*/
        [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: false,
                pinned: false,
            },
            {
                name: ModifyReasonsListDataGridCellKeys.defaultColumn,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                sortable: false,
                width: 0,
            },
            {
                title: "Code",
                name: ModifyReasonsListDataGridCellKeys.code,
                type: DataGridColumnTypes.element,
                width: 100,
                sortable: true,
            },
            {
                title: "Reason",
                name: ModifyReasonsListDataGridCellKeys.reason,
                type: DataGridColumnTypes.element,
                width: 240,
                sortable: true,
            },
            {
                title: "Operator",
                name: ModifyReasonsListDataGridCellKeys.operator,
                type: DataGridColumnTypes.string,
                width: 150,
                sortable: true,
            },
            {
                title: "Added Date",
                name: ModifyReasonsListDataGridCellKeys.addedDate,
                type: DataGridColumnTypes.dateTime,
                width: 150,
                sortable: true,
            },

        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    })

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo, [dataGridPageSize]);

    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
    const [orderBy, setOrderBy] = useState(dataGridSortBy);

    /**
     * Memo version of the orderByMethod compare function
     * @type {function(a, b): number}
     * */
    const orderByMethod = useMemo(() => {
        switch (orderBy?.field) {
            case ModifyReasonsListDataGridCellKeys.code:
                return (a, b) =>
                    orderBy?.descending
                        ? stringComparator(a?.code ?? '', b?.code ?? '')
                        : stringComparator(b?.code ?? '', a?.code ?? '')
            case ModifyReasonsListDataGridCellKeys.reason:
                return (a, b) =>
                    orderBy?.descending
                        ? stringComparator(a?.title ?? '', b?.title ?? '')
                        : stringComparator(b?.title ?? '', a?.title ?? '')
            case ModifyReasonsListDataGridCellKeys.operator:
                return (a, b) =>
                    orderBy?.descending
                        ? stringComparator(a?.modifiedBy ?? '', b?.modifiedBy ?? '')
                        : stringComparator(b?.modifiedBy ?? '', a?.modifiedBy ?? '')
            case ModifyReasonsListDataGridCellKeys.addedDate:
                return (a, b) =>
                    orderBy?.descending
                        ? dateComparator(a?.modifiedDateTime ?? '', b?.modifiedDateTime ?? '')
                        : dateComparator(b?.modifiedDateTime ?? '', a?.modifiedDateTime ?? '')
            default:
                return (a, b) => 0
        }
    }, [orderBy?.field, orderBy?.descending])

    /**
     * With each change in the [allReasonsProp]:
     * Sets the [allReasons] in the inner state
     * Updates the
     */
    useEffect(() => {
        setAllReasons(allReasonsProp)
    }, [allReasonsProp])

    /**
     * When the dialog gets opened:
     * Resets the pagination and sorting for the showing the list of reasons
     */
    useEffect(() => {
        if (open) {
            setPaginationInfo(prevState => ({
                ...prevState,
                currentPage: _initialPaginationInfo.currentPage,
                length: allReasonsProp?.length
            }))
            setOrderBy(dataGridSortBy);
        }
    }, [open])

    /**
     * With each change in the pagination info current page, page size, and sorting:
     * Formats the records and sets the [visibleReasons] in the state
     */
    useEffect(() => {
        if (paginationInfo.currentPage === _initialPaginationInfo.currentPage) {
            setPaginationInfo(prevState => ({
                ...prevState,
                currentPage: 1
            }))
        }
        const entities = Array.from(allReasons).sort(orderByMethod)
        const start = Math.min(Math.max((paginationInfo.currentPage - 1) * paginationInfo.pageSize, 0), entities.length);
        const end = Math.min((paginationInfo.currentPage) * paginationInfo.pageSize, entities.length);
        setVisibleReasons(entities?.slice(start, end)?.map(e => ({...e, isLoading: false})) ?? []);
    }, [paginationInfo.currentPage, paginationInfo.pageSize, orderByMethod, allReasons])

    /**
     * Sorts the entries of the credits in place.
     * @param {DataGridSortBy} tableOrderBy
     */
    const onSort = useCallback((tableOrderBy) => {
        setOrderBy(tableOrderBy);
        setPaginationInfo(prevState => ({...prevState, currentPage: 1}));
    }, [])

    /**
     * Updates the reason record based on the received params
     * @param {number} id the id of the record
     * @param {string} value the new value of the field
     * @param {boolean} isReason indicating if the updated field was reason
     * @return {Promise<void>}
     */
    const updateReason = async (id, value, isReason = false) => {
        if (!canManageAdjustmentReasons) return;
        setVisibleReasons(prevState => prevState.map(e => e.id === id ? {...e, isLoading: true} : e))
        const forApi = {
            ...(allReasons.find(e => e.id === id)),
            ...(isReason ? {title: value} : {code: value}),
            updatedProperties: [
                isReason ? 'Title' : 'Code'
            ]
        }
        if (isReason) {
            delete forApi.code;
        }
        const response = await Api.updateInventoryAdjustmentReason(forApi);
        if (!isMounted())
            return;
        setVisibleReasons(prevState => prevState.map(e => e.id === id ? {...e, isLoading: false} : e))
        if (response?.resultFlag) {
            setVisibleReasons(prevState => prevState.map(e => e.id === id
                ? {...e, isLoading: false, ...(isReason ? {title: value} : {code: value})}
                : e
            ))
            setAllReasons(prevState => prevState.map(e => e.id === id
                ? {...e, isLoading: false, ...(isReason ? {title: value} : {code: value})}
                : e
            ))
        }

    }

    /**
     * Commits the edited value of the provided cell belonging to the [rowKey] and [colName]
     * @param prevValue
     * @param newValue
     * @param rowKey
     * @param colName
     * @return {Promise<void>}
     */
    const onRecordEdited = async (prevValue, newValue, rowKey, colName) => {
        const editedRecordId = visibleReasons.find(e => e.id === rowKey)?.id;
        switch (colName) {
            case ModifyReasonsListDataGridCellKeys.code:
                return updateReason(editedRecordId, newValue)
            case ModifyReasonsListDataGridCellKeys.reason:
                return updateReason(editedRecordId, newValue, true)
            default:
                break;
        }
    }

    /**
     * Sets the reason as default reason in the list
     * @param reason
     * @return {Promise<void>}
     */
    const setReasonAsDefault = async (reason) => {
        if (!canManageAdjustmentReasons) return;
        setVisibleReasons(prevState => prevState.map(e => e.id === reason.id ? {...reason, isLoading: true} : e))
        const forApi = {
            id: reason.id,
            isDefault: true,
            updatedProperties: [
                "IsDefault"
            ]
        }
        const response = await Api.updateInventoryAdjustmentReason(forApi);
        if (!isMounted())
            return;
        setVisibleReasons(prevState => prevState.map(e => e.id === reason.id ? {...reason, isLoading: false} : e))
        if (response?.resultFlag) {
            setVisibleReasons(prevState => prevState.map(e => e.id === reason.id ? {...e, isDefault: true} : {...e, isDefault: false}))
            setAllReasons(prevState => prevState.map(e => e.id === reason.id ? {...e, isDefault: true} : {...e, isDefault: false}))
        }
    }

    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     */
    const getRemoveConfirmation = (item) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected Reason?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && _removeReasons(item),
        );
    }

    /**
     * Calls the api regarding removing the list of drivers form the list
     * @param reason the selected reason to be removed
     * @private
     */
    const _removeReasons = async (reason) => {
        if (!canManageAdjustmentReasons) return;
        setLoading(true);
        const response = await Api.removeInventoryAdjustmentsReason(reason.id);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setAllReasons(prevState => prevState.filter(e => e.id !== reason.id))
            setVisibleReasons(prevState => prevState.filter(e => e.id !== reason.id))
        }
    }

    /**
     * Handles performing the invoked action over the reasons in the list
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @return {Promise<void>}
     */
    const onActionInvoked = async (action, selectedRows, excludedRows, allRowsSelected) => {
        let _data;
        if (allRowsSelected) {
            // because we have singleSelectionOnly, then we know that pagination.length === 1
            _data = visibleReasons[0];
        } else {
            _data = selectedRows[0].data;
        }
        switch (action.name) {
            case ModifyAvailabilityReasonsListActionNames.SetAsDefault:
                setReasonAsDefault(_data).then();
                break;
            case ModifyAvailabilityReasonsListActionNames.Delete:
                getRemoveConfirmation(_data);
                break;
            default:
                break;
        }
    }

    /**
     * Handles adding a new reason to the list of reasons
     * @param reason
     */
    const handleAddingNewReason = (reason) => {
        setVisibleReasons(prevState => [reason, ...prevState])
        setAllReasons(prevState => [...prevState, reason])
    }

    /**
     * Handles closing the dialog and calls the passed callback funciton for updating the reasons
     */
    const closedDialog = () => {
        onDialogClosed(allReasons);
        setOpen(false);
    }

    /**
     * Memo version of data grid Rows
     * @type {DataGridRow[]}
     */
    const rows = useMemo(() => {
        return visibleReasons?.map((reason) => ({
            key: reason.id,
            data: reason,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [ModifyReasonsListDataGridCellKeys.defaultColumn]: reason?.isDefault
                    ? <DefaultIcon/>
                    : <></>,
                [ModifyReasonsListDataGridCellKeys.code]: {
                    value: ({openEditPopover}) => <>
                        <IconButton
                            onPressed={openEditPopover}
                            disabled={reason.isLoading}
                            color={'secondary'}
                        >
                            {
                                reason.isLoading
                                    ? <CircularProgress size={20} color={'secondary'}/>
                                    : <Edit className={"icon small"}/>
                            }
                        </IconButton>
                        <p {...dataGridCellTextProps} className={'ml-2'}>
                            {reason?.code ?? ''}
                        </p>
                    </>,
                    editable: true,
                    editableValue: reason.code,
                    EditableContentComponent: <ReasonAndCodeEditor/>

                },
                [ModifyReasonsListDataGridCellKeys.reason]: {
                    value: ({openEditPopover}) => <>
                        <IconButton
                            onPressed={openEditPopover}
                            disabled={reason.isLoading}
                            color={'secondary'}
                        >
                            {
                                reason.isLoading
                                    ? <CircularProgress size={20} color={'secondary'}/>
                                    : <Edit className={"icon small"}/>
                            }
                        </IconButton>
                        <p {...dataGridCellTextProps} className={'ml-2'}>
                            {reason?.title ?? ''}
                        </p>
                    </>,
                    editable: true,
                    editableValue: reason.title,
                    EditableContentComponent: <ReasonAndCodeEditor isReason={true}/>

                },
                [ModifyReasonsListDataGridCellKeys.operator]: reason?.modifiedBy,
                [ModifyReasonsListDataGridCellKeys.addedDate]: reason?.modifiedDateTime,
            }
        }))
    }, [visibleReasons])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: ModifyAvailabilityReasonsListActionNames.SetAsDefault,
                title: 'Set as Default',
                visibilityConstraints: (selectedRows, excludedRows, allRowsSelected, pagination) => {
                    if (allRowsSelected && pagination.length === 1) {
                        return (!visibleReasons[0]?.isDefault) ?? false;
                    }
                    return (selectedRows[0]?.data?.isDefault ?? false);
                },
            },
            {
                name: ModifyAvailabilityReasonsListActionNames.Delete,
                title: 'Delete',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [visibleReasons])

    return (
        <Dialog
            open={open}
            setOpen={closedDialog}
            maxWidth={'md'}
            className={'bound-dialog'}
        >
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={`text-lg text-color-1 my-4 font-weight-semi-bold text-center`}>
                    Modify Reasons List
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent>
                <Row className='no-gutters align-items-center w-100 px-3'>
                    <Row className='no-gutters justify-content-center align-items-center w-100'>
                        <div className={'d-flex align-items-center justify-content-center w-100'}>
                            <AddNewReasonSection handleAddingNewReason={handleAddingNewReason}/>
                        </div>
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            initialLayoutState={{
                                columns: columns,
                                density: DataGridDensities.compact,
                            }}
                            singleSelectionOnly
                            visibleRows={5}
                            rows={rows}
                            columns={dataGridColumns}
                            hideToolbar
                            footerActions={footerActions}
                            hideFooterActions={visibleReasons?.length < 1}
                            onActionInvoked={onActionInvoked}
                            state={{
                                pagination: paginationInfo,
                                sortBy: orderBy,
                                loading: {state: loading},
                                density: dataGridDensity ?? DataGridDensities.compact,
                            }}
                            onCellEdited={onRecordEdited}
                            onSortByChanged={(_, current) => onSort(current)}
                            onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                            onPageSizeChanged={(_, current) => setPaginationInfo(current)}
                        />
                        <div className='my-1 w-100'/>
                    </Row>
                </Row>
            </DialogContent>
            <DialogActions>
                <Row className='no-gutters justify-content-center align-items-center w-100'>
                    <Divider className={'divider'}/>
                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                        <Button
                            disabled={!open || loading}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={closedDialog}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Close
                        </Button>
                    </Row>
                </Row>
            </DialogActions>
        </Dialog>
    );
}

const ReasonAndCodeEditor = ({value, setValue, setCanSubmitChanges, isReason = false}) => {
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value can be submitted
     *   which is possible when the input value is valid and its length is less than dedicated number of characters
     */
    useLayoutEffect(() => {
        setCanSubmitChanges(!!(value?.trim()) && value.length <= (isReason ? maxReasonLength : maxCodeLength));
    }, [value])

    /**
     * Changes the value of this editor.
     * - Checks if entered value's length is less than the maximum characters
     * @param value
     */
    const onValueChanged = (value) => {
        if (!value || (value?.trim()?.length ?? 0) < 1) {
            setShowErrorMessage(true);
            return setValue(undefined);
        }
        if (value.length > (isReason ? maxReasonLength : maxCodeLength))
            return;
        setShowErrorMessage(false);
        setValue(value)
    }

    return (
        <>
            <OutlinedInput
                fullWidth
                multiline={isReason}
                size={'small'}
                label={isReason ? "New Reason" : 'New Code'}
                helperText={showErrorMessage ? 'Field cannot be empty' : ''}
                value={value ?? ''}
                onChange={onValueChanged}
                type={'text'}
            />
        </>
    );
}


const formNames = {
    code: 'code',
    reason: 'reason',
}
const schema = Yup.object().shape({
    [formNames.code]: Yup.string().test('code', `code should be less than ${maxCodeLength} characters `,
        (val) => {
            if (!val || val?.trim()?.length < 1)
                return false;
            return val?.length <= maxCodeLength;
        }
    ).nullable().required(ValidateMessages.required),
    [formNames.reason]: Yup.string().test('reason', ` reason should be less than ${maxReasonLength} characters `,
        (val) => {
            if (!val || val?.trim()?.length < 1)
                return false;
            return val?.length <= maxReasonLength;
        }
    ).nullable().required(ValidateMessages.required),
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const AddNewReasonSection = ({handleAddingNewReason}) => {
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManageAdjustmentReasons = packages.bizInventory.manageAdjustmentReasons;
    /*** @type {React.MutableRefObject<formApi>}*/
    const formRef = useRef(null);

    /**
     * with initial render of the component:
     * Attaches a resize observer
     */
    useEffect(() => {
        const reasonWrapper = document.getElementsByClassName('reason-field')[0]
        const reasonInput = reasonWrapper.getElementsByTagName('div')[0]
        const observer = ObserversService.newResizeObserver(matchFieldsHeight);
        ObserversService.observeResizeObserver(observer, reasonInput);

        return () => ObserversService.disconnectResizeObserver(observer);
    }, [])

    /**
     * Handles setting the height of code field same as the reason field
     * @param entries
     */
    const matchFieldsHeight = (entries) => {
        if (!entries?.length)
            return;
        const codeWrapper = document.getElementsByClassName('code-field')[0]
        if (!codeWrapper)
            return;
        const codeInput = codeWrapper.getElementsByTagName('div')[0]
        if (codeInput)
            codeInput.style.height = `${entries?.[0].target.clientHeight}px`
    }

    /**
     * Submits the form values for creating a new task:
     * - Calls the function for creating the task with the title
     * - Handles updating the [loading] in the inner state
     * @param values
     * @return {Promise<void>}
     */
    const onSubmit = async (values) => {
        if (!canManageAdjustmentReasons) return;
        setLoading(true);
        const forApi = {
            code: values[formNames.code],
            title: values[formNames.reason]
        }
        const response = await Api.createInventoryAdjustmentReason(forApi);
        if (!isMounted())
            return;
        setTimeout(() => formRef.current.restart({}), 0)
        setLoading(false);
        if (response?.resultFlag) {
            handleAddingNewReason(response?.data);
        }
    }

    return (
        <Form
            className={'d-flex align-items-center justify-content-center w-100'}
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{}}
            render={({submitting, values, form}) => {
                formRef.current = form;
                return (
                    <Row className='no-gutters d-flex align-items-start justify-content-center mt-4 w-100'>
                        <Col xs={12} className={'mb-3'}>
                            <p className={'text-md font-weight-semi-bold'}>New Reason</p>
                        </Col>
                        <Col xs={3} className={'px-2 mb-3'}>
                            <OutlinedInputForm
                                classes={{
                                    root: 'code-field'
                                }}
                                disabled={loading}
                                value={values[formNames.code] ?? ''}
                                label={'Code'}
                                name={formNames.code}
                                type={'text'}
                                required={required[formNames.code]}
                            />
                        </Col>
                        <Col xs={9} className={'px-2 mb-3'}>
                            <OutlinedInputForm
                                classes={{
                                    root: 'reason-field'
                                }}
                                multiline
                                maxRows={2}
                                disabled={loading}
                                value={values[formNames.reason] ?? ''}
                                label={'Reason'}
                                name={formNames.reason}
                                type={'text'}
                                required={required[formNames.reason]}
                            />
                        </Col>
                        <div className={'d-flex w-100 justify-content-center align-items-center px-2'}>
                            <Button
                                disabled={!!loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className={'px-5'}
                            >
                                {
                                    submitting
                                        ? "Adding..."
                                        : "Add"
                                }
                            </Button>
                        </div>
                    </Row>
                );
            }}
        />
    )
}
export default ModifyAvailabilityReasonsDialog;
